import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getProjects, getClients, getUserNames, createProject, updateProject, deleteProject, duplicateProject } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatTime, formatCurrency } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Progress } from '../components/ui/progress';
import { Plus, Edit, Trash2, Eye, Search, Clock, FolderKanban, Copy, Lock, AlertTriangle, Archive, Play } from 'lucide-react';
import { useTimer } from '../lib/TimerContext';
import { toast } from 'sonner';

const Projects = () => {
  const { hasPermission, user } = useAuth();
  const isAdmin = user?.is_admin;
  const { isRunning, startTimer } = useTimer();
  const [searchParams] = useSearchParams();
  const clientFilter = searchParams.get('client');
  
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active_completed'); // Default: active + completed
  const [showArchived, setShowArchived] = useState(false);
  const [showIncomplete, setShowIncomplete] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    client_id: clientFilter || '',
    description: '',
    status: 'active',
    start_date: '',
    end_date: '',
    estimated_revenue: '',
    actual_revenue: '',
    max_hours: '',
    assigned_users: [],
  });

  const fetchData = async () => {
    try {
      const params = { include_archived: showArchived };
      if (clientFilter) params.client_id = clientFilter;
      if (statusFilter === 'active_completed') {
        params.statuses = 'active,completed';
      } else if (statusFilter && statusFilter !== 'all') {
        params.status = statusFilter;
      }
      if (showIncomplete) params.incomplete_only = true;
      
      const [projectsRes, clientsRes, usersRes] = await Promise.all([
        getProjects(params),
        getClients(),
        getUserNames().catch(() => ({ data: [] })),
      ]);
      setProjects(projectsRes.data);
      setClients(clientsRes.data);
      setUsers(usersRes.data || []);
    } catch (error) {
      toast.error('Chyba při načítání projektů');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [clientFilter, statusFilter, showArchived, showIncomplete]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        estimated_revenue: formData.estimated_revenue ? parseFloat(formData.estimated_revenue) : null,
        actual_revenue: formData.actual_revenue ? parseFloat(formData.actual_revenue) : null,
        max_hours: formData.max_hours ? parseFloat(formData.max_hours) : null,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        assigned_users: formData.assigned_users.length > 0 ? formData.assigned_users : null,
      };
      if (editingProject) {
        await updateProject(editingProject.id, data);
        toast.success('Projekt aktualizován');
      } else {
        await createProject(data);
        toast.success('Projekt vytvořen');
      }
      setDialogOpen(false);
      setEditingProject(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Chyba při ukládání');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      client_id: clientFilter || '',
      description: '',
      status: 'active',
      start_date: '',
      end_date: '',
      estimated_revenue: '',
      actual_revenue: '',
      max_hours: '',
      assigned_users: [],
    });
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      name: project.name,
      client_id: project.client_id,
      description: project.description || '',
      status: project.status,
      start_date: project.start_date || '',
      end_date: project.end_date || '',
      estimated_revenue: project.estimated_revenue || '',
      actual_revenue: project.actual_revenue || '',
      max_hours: project.max_hours || '',
      assigned_users: project.assigned_users || [],
    });
    setDialogOpen(true);
  };

  const handleDuplicate = async (project) => {
    try {
      await duplicateProject(project.id);
      toast.success('Projekt zduplikován');
      fetchData();
    } catch (error) {
      toast.error('Chyba při duplikování projektu');
    }
  };

  const handleDelete = async () => {
    if (!projectToDelete) return;
    try {
      await deleteProject(projectToDelete.id);
      toast.success('Projekt smazán');
      fetchData();
    } catch (error) {
      toast.error('Chyba při mazání projektu');
    }
    setDeleteDialogOpen(false);
    setProjectToDelete(null);
  };

  const toggleUserAssignment = (userId) => {
    setFormData(prev => ({
      ...prev,
      assigned_users: prev.assigned_users.includes(userId)
        ? prev.assigned_users.filter(id => id !== userId)
        : [...prev.assigned_users, userId]
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800 border-green-300';
      case 'completed': return 'bg-blue-100 text-blue-800 border-blue-300';
      case 'paused': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'potential': return 'bg-purple-100 text-purple-800 border-purple-300';
      case 'future': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'invoiced': return 'bg-[#006CB6] text-white border-[#006CB6]';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active': return 'Aktivní';
      case 'completed': return 'Dokončeno';
      case 'paused': return 'Pozastaveno';
      case 'potential': return 'Potenciální';
      case 'future': return 'Budoucí';
      case 'invoiced': return 'Fakturováno';
      default: return status;
    }
  };

  const filteredProjects = projects.filter(project =>
    project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    project.client_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getCapacityPercent = (project) => {
    if (!project.max_hours) return null;
    return Math.min((project.used_hours / project.max_hours) * 100, 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="font-oswald text-xl uppercase tracking-wide">Načítání...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="projects-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-oswald text-4xl font-bold uppercase tracking-tight">Projekty</h1>
          <p className="font-serif text-gray-600 mt-1">
            {clientFilter ? `Projekty vybraného klienta` : 'Správa všech projektů'}
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-[#F4E600] text-black font-oswald uppercase tracking-wide border-2 border-black rounded-none shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all"
              onClick={() => { setEditingProject(null); resetForm(); }}
              data-testid="add-project-btn"
            >
              <Plus size={18} className="mr-2" />
              Nový projekt
            </Button>
          </DialogTrigger>
          <DialogContent className="border-2 border-black rounded-none shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-oswald text-xl uppercase tracking-tight">
                {editingProject ? 'Upravit projekt' : 'Nový projekt'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label className="font-oswald uppercase text-xs tracking-wide">Název projektu *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="border-2 border-gray-200 focus:border-black rounded-none"
                  data-testid="project-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="font-oswald uppercase text-xs tracking-wide">Klient *</Label>
                  <Select
                    value={formData.client_id}
                    onValueChange={(v) => setFormData({ ...formData, client_id: v })}
                  >
                    <SelectTrigger className="border-2 border-gray-200 rounded-none" data-testid="project-client-select">
                      <SelectValue placeholder="Vyberte klienta" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="font-oswald uppercase text-xs tracking-wide">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(v) => setFormData({ ...formData, status: v })}
                  >
                    <SelectTrigger className="border-2 border-gray-200 rounded-none" data-testid="project-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="potential">Potenciální</SelectItem>
                      <SelectItem value="future">Budoucí</SelectItem>
                      <SelectItem value="active">Aktivní</SelectItem>
                      <SelectItem value="completed">Dokončeno</SelectItem>
                      <SelectItem value="invoiced">Fakturováno</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="font-oswald uppercase text-xs tracking-wide">Popis</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="border-2 border-gray-200 focus:border-black rounded-none"
                  data-testid="project-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="font-oswald uppercase text-xs tracking-wide">Datum začátku</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="border-2 border-gray-200 focus:border-black rounded-none"
                    data-testid="project-start-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="font-oswald uppercase text-xs tracking-wide">Datum konce</Label>
                  <Input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="border-2 border-gray-200 focus:border-black rounded-none"
                    data-testid="project-end-date-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="font-oswald uppercase text-xs tracking-wide">Maximální kapacita (hodiny)</Label>
                <Input
                  type="number"
                  value={formData.max_hours}
                  onChange={(e) => setFormData({ ...formData, max_hours: e.target.value })}
                  placeholder="Např. 100"
                  className="border-2 border-gray-200 focus:border-black rounded-none"
                  data-testid="project-max-hours-input"
                />
                <p className="text-xs text-gray-500 font-serif">Pro sledování vyčerpání kapacity</p>
              </div>
              {hasPermission('revenue') && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="font-oswald uppercase text-xs tracking-wide">Odhadované tržby (CZK)</Label>
                    <Input
                      type="number"
                      value={formData.estimated_revenue}
                      onChange={(e) => setFormData({ ...formData, estimated_revenue: e.target.value })}
                      placeholder="0"
                      className="border-2 border-gray-200 focus:border-black rounded-none"
                      data-testid="project-estimated-revenue-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="font-oswald uppercase text-xs tracking-wide">Reálné tržby (CZK)</Label>
                    <Input
                      type="number"
                      value={formData.actual_revenue}
                      onChange={(e) => setFormData({ ...formData, actual_revenue: e.target.value })}
                      placeholder="0"
                      className="border-2 border-gray-200 focus:border-black rounded-none"
                      data-testid="project-actual-revenue-input"
                    />
                  </div>
                </div>
              )}
              {/* Assign Users */}
              {users.length > 0 && (
                <div className="space-y-2">
                  <Label className="font-oswald uppercase text-xs tracking-wide">Přiřazení uživatelé</Label>
                  <div className="border-2 border-gray-200 p-3 max-h-40 overflow-y-auto space-y-2">
                    {users.map((u) => (
                      <div key={u.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`user-${u.id}`}
                          checked={formData.assigned_users.includes(u.id)}
                          onCheckedChange={() => toggleUserAssignment(u.id)}
                        />
                        <label htmlFor={`user-${u.id}`} className="font-serif text-sm cursor-pointer">
                          {u.name}
                        </label>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 font-serif">Přiřazení uživatelé uvidí tento projekt</p>
                </div>
              )}
              <Button
                type="submit"
                className="w-full bg-black text-white font-oswald uppercase tracking-wide rounded-none hover:bg-gray-800"
                data-testid="project-submit-btn"
              >
                {editingProject ? 'Uložit změny' : 'Vytvořit projekt'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <Input
            placeholder="Hledat projekty..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 border-2 border-gray-200 focus:border-black rounded-none font-serif"
            data-testid="search-projects"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48 border-2 border-gray-200 rounded-none" data-testid="filter-status">
            <SelectValue placeholder="Filtr statusu" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active_completed">Aktivní a dokončeno</SelectItem>
            <SelectItem value="all">Všechny stavy</SelectItem>
            <SelectItem value="potential">Potenciální</SelectItem>
            <SelectItem value="future">Budoucí</SelectItem>
            <SelectItem value="active">Aktivní</SelectItem>
            <SelectItem value="completed">Dokončeno</SelectItem>
            <SelectItem value="invoiced">Fakturováno (uzamčeno)</SelectItem>
          </SelectContent>
        </Select>
        
        {/* Additional Filters */}
        {isAdmin && (
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm font-serif cursor-pointer">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                className="rounded border-gray-300"
              />
              <Archive size={14} className="text-gray-500" />
              Archivované
            </label>
            <label className="flex items-center gap-2 text-sm font-serif cursor-pointer">
              <input
                type="checkbox"
                checked={showIncomplete}
                onChange={(e) => setShowIncomplete(e.target.checked)}
                className="rounded border-gray-300"
              />
              <AlertTriangle size={14} className="text-yellow-500" />
              Neúplné
            </label>
          </div>
        )}
      </div>

      {/* Projects Grid */}
      {filteredProjects.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-gray-300 p-12 text-center">
          <FolderKanban size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="font-oswald text-lg uppercase tracking-wide text-gray-500">
            {searchTerm || statusFilter !== 'all' ? 'Žádné projekty nenalezeny' : 'Zatím nemáte žádné projekty'}
          </h3>
          <p className="font-serif text-gray-400 mt-2">
            Vytvořte první projekt kliknutím na tlačítko výše
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project) => {
            const capacityPercent = getCapacityPercent(project);
            return (
              <div
                key={project.id}
                className={`bg-white border-2 p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all group ${
                  project.is_archived ? 'border-gray-300 opacity-60' : 
                  project.is_locked ? 'border-[#006CB6]' : 'border-black'
                }`}
                data-testid={`project-card-${project.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/projects/${project.id}`}
                        className="font-oswald text-lg font-bold uppercase tracking-tight hover:text-[#006CB6]"
                      >
                        {project.name}
                      </Link>
                      {/* Status Icons */}
                      {project.is_locked && (
                        <span title="Fakturováno - uzamčeno">
                          <Lock size={14} className="text-[#006CB6]" />
                        </span>
                      )}
                      {project.is_incomplete && !project.is_locked && (
                        <span title="Neúplný projekt - chybí údaje">
                          <AlertTriangle size={14} className="text-yellow-500" />
                        </span>
                      )}
                      {project.is_archived && (
                        <span title="Archivovaný projekt">
                          <Archive size={14} className="text-gray-400" />
                        </span>
                      )}
                    </div>
                    <div className="font-serif text-sm text-gray-500">{project.client_name}</div>
                  </div>
                  <span className={`px-2 py-1 text-xs font-oswald uppercase border ${getStatusColor(project.status)}`}>
                    {getStatusLabel(project.status)}
                  </span>
                </div>

                {/* Capacity Bar */}
                {capacityPercent !== null && (
                  <div className="mb-3">
                    <div className="flex justify-between text-xs font-oswald mb-1">
                      <span>Kapacita</span>
                      <span className={capacityPercent >= 90 ? 'text-red-600' : ''}>
                        {project.used_hours}h / {project.max_hours}h
                      </span>
                    </div>
                    <Progress 
                      value={capacityPercent} 
                      className={`h-2 ${capacityPercent >= 90 ? '[&>div]:bg-red-500' : capacityPercent >= 70 ? '[&>div]:bg-yellow-500' : '[&>div]:bg-green-500'}`}
                    />
                  </div>
                )}

                <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                  <div className="flex items-center gap-1">
                    <Clock size={14} />
                    <span className="font-serif">{formatTime(project.total_time_minutes)}</span>
                  </div>
                </div>

                <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                  <div className="flex gap-1">
                    <Link
                      to={`/projects/${project.id}`}
                      className="p-2 hover:bg-gray-100 border border-gray-200"
                      data-testid={`view-project-${project.id}`}
                    >
                      <Eye size={16} />
                    </Link>
                    {isAdmin && (
                      <>
                        <button
                          onClick={() => handleEdit(project)}
                          className="p-2 hover:bg-gray-100 border border-gray-200"
                          data-testid={`edit-project-${project.id}`}
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDuplicate(project)}
                          className="p-2 hover:bg-blue-100 border border-gray-200 text-blue-600"
                          data-testid={`duplicate-project-${project.id}`}
                        >
                          <Copy size={16} />
                        </button>
                        <button
                          onClick={() => { setProjectToDelete(project); setDeleteDialogOpen(true); }}
                          className="p-2 hover:bg-red-100 border border-gray-200 text-red-500"
                          data-testid={`delete-project-${project.id}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      </>
                    )}
                  </div>
                  {project.status === 'active' && (
                    <button
                      onClick={() => startTimer({ project_id: project.id, project_name: project.name })}
                      disabled={isRunning}
                      title={isRunning ? 'Stopky již běží' : 'Spustit stopky'}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-[#F4E600] text-black font-oswald uppercase text-xs tracking-wide border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-x-0 disabled:hover:translate-y-0 disabled:hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]"
                    >
                      <Play size={12} />
                      Stopky
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="border-2 border-black rounded-none shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-oswald uppercase">Smazat projekt?</AlertDialogTitle>
            <AlertDialogDescription className="font-serif">
              Opravdu chcete archivovat projekt &ldquo;{projectToDelete?.name}&rdquo;? Projekt bude skrytý, ale data zůstanou zachována.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-oswald uppercase rounded-none border-2 border-gray-200">
              Zrušit
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 font-oswald uppercase rounded-none hover:bg-red-600"
            >
              Smazat
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Projects;
